import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { canViewAdminSettings } from '../../permissions';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Admin controls for the Standard/Advanced reporting mode + Power BI embed
// configuration. Moved out of SettingsPage 2026-04-21 to consolidate all
// admin tooling behind /admin/* routes.
export default function ReportingAdminPage() {
  const { user, getHeaders } = useAuth();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedKey, setSavedKey] = useState(null);

  const [embedUrl, setEmbedUrl] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [reportId, setReportId] = useState('');

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/settings`, { headers: getHeaders() });
      if (res.ok) setSettings(await res.json());
    } catch (err) { /* leave previous */ }
    setLoading(false);
  }, [getHeaders]);

  useEffect(() => {
    if (canViewAdminSettings(user)) fetchSettings();
    else setLoading(false);
  }, [user, fetchSettings]);

  const getVal = (key, fallback = '') => settings.find(s => s.key === key)?.value ?? fallback;
  const mode = getVal('bi_reporting_mode', 'standard');
  const method = getVal('bi_embed_method', 'iframe');

  useEffect(() => {
    setEmbedUrl(getVal('bi_powerbi_embed_url'));
    setTenantId(getVal('bi_powerbi_tenant_id'));
    setClientId(getVal('bi_powerbi_client_id'));
    setWorkspaceId(getVal('bi_powerbi_workspace_id'));
    setReportId(getVal('bi_powerbi_report_id'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  async function save(key, value) {
    try {
      await fetch(`${API_BASE}/settings/${key}`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ value: value || '', isSecret: false }),
      });
      await fetchSettings();
      setSavedKey(key);
      setTimeout(() => setSavedKey(k => (k === key ? null : k)), 2000);
    } catch (err) { alert('Save failed: ' + err.message); }
  }

  if (!canViewAdminSettings(user)) {
    return (
      <div className="page-content">
        <div className="empty-state"><h3>Admin only</h3><p>Reporting config is admin-only.</p></div>
      </div>
    );
  }

  return (
    <>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Link to="/settings" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>&larr; Settings</Link>
          <h2>Reporting</h2>
        </div>
      </div>

      <div className="page-content" style={{ maxWidth: '760px' }}>
        {loading && <div style={{ padding: '20px', color: '#777' }}>Loading...</div>}

        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header">
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Reporting</h3>
          </div>
          <div className="card-body">
            <div style={{ fontSize: '12px', color: '#777', marginBottom: '12px', lineHeight: 1.5 }}>
              <strong>Standard</strong> uses BidBoard&apos;s built-in analytics page. <strong>Advanced</strong> embeds a Power BI report. Users can flip between the two on the Analytics page; this setting controls the default.
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--color-gray-100)' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>Default Mode</div>
                <div style={{ fontSize: '12px', color: '#777' }}>Which analytics view is shown by default</div>
              </div>
              <div style={{ display: 'flex', gap: '4px', background: 'var(--color-gray-100)', padding: '3px', borderRadius: '6px' }}>
                {['standard', 'advanced'].map(opt => (
                  <button
                    key={opt}
                    onClick={() => save('bi_reporting_mode', opt)}
                    style={{
                      padding: '5px 14px', fontSize: '12px', fontWeight: 500,
                      border: 'none', borderRadius: '4px', cursor: 'pointer',
                      background: mode === opt ? 'var(--color-white)' : 'transparent',
                      color: mode === opt ? 'var(--color-black)' : 'var(--color-gray-500)',
                      boxShadow: mode === opt ? 'var(--shadow-sm)' : 'none',
                      textTransform: 'capitalize',
                    }}
                  >{opt}</button>
                ))}
              </div>
            </div>

            {mode === 'advanced' && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--color-gray-100)' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 500 }}>Embed Method</div>
                  <div style={{ fontSize: '12px', color: '#777' }}>
                    <strong>iframe</strong> &mdash; paste a Secure embed URL, zero creds<br/>
                    <strong>SDK</strong> &mdash; MSAL + powerbi-client, needs Azure AD app reg
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px', background: 'var(--color-gray-100)', padding: '3px', borderRadius: '6px' }}>
                  {[{ v: 'iframe', label: 'iframe' }, { v: 'sdk', label: 'SDK' }].map(opt => (
                    <button
                      key={opt.v}
                      onClick={() => save('bi_embed_method', opt.v)}
                      style={{
                        padding: '5px 14px', fontSize: '12px', fontWeight: 500,
                        border: 'none', borderRadius: '4px', cursor: 'pointer',
                        background: method === opt.v ? 'var(--color-white)' : 'transparent',
                        color: method === opt.v ? 'var(--color-black)' : 'var(--color-gray-500)',
                        boxShadow: method === opt.v ? 'var(--shadow-sm)' : 'none',
                      }}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
            )}

            {mode === 'advanced' && method === 'iframe' && (
              <ReportingField
                label="Secure Embed URL"
                hint="Power BI Service → open your report → File → Embed report → Website or portal → copy the URL"
                value={embedUrl} onChange={setEmbedUrl}
                onSave={() => save('bi_powerbi_embed_url', embedUrl)}
                saved={savedKey === 'bi_powerbi_embed_url'}
                placeholder="https://app.powerbi.com/reportEmbed?reportId=…&autoAuth=true&ctid=…"
              />
            )}

            {mode === 'advanced' && method === 'sdk' && (
              <>
                <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', padding: '10px 12px', borderRadius: '6px', fontSize: '12px', color: '#78350f', margin: '12px 0', lineHeight: 1.5 }}>
                  SDK mode is Phase 2. Save these now and the wiring can be added later without reconfiguring.
                </div>
                <ReportingField label="Tenant ID" hint="Entra → Overview → Directory (tenant) ID"
                  value={tenantId} onChange={setTenantId}
                  onSave={() => save('bi_powerbi_tenant_id', tenantId)}
                  saved={savedKey === 'bi_powerbi_tenant_id'}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                <ReportingField label="Client (Application) ID" hint="Entra → App registrations → your BidBoard app → Overview"
                  value={clientId} onChange={setClientId}
                  onSave={() => save('bi_powerbi_client_id', clientId)}
                  saved={savedKey === 'bi_powerbi_client_id'}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                <ReportingField label="Workspace ID" hint="In Power BI Service, open the workspace → URL shows …/groups/{id}/…"
                  value={workspaceId} onChange={setWorkspaceId}
                  onSave={() => save('bi_powerbi_workspace_id', workspaceId)}
                  saved={savedKey === 'bi_powerbi_workspace_id'}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                <ReportingField label="Report ID" hint="Open the report in Service → URL shows …/reports/{id}/…"
                  value={reportId} onChange={setReportId}
                  onSave={() => save('bi_powerbi_report_id', reportId)}
                  saved={savedKey === 'bi_powerbi_report_id'}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function ReportingField({ label, hint, value, onChange, onSave, saved, placeholder }) {
  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid var(--color-gray-100)' }}>
      <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '11px', color: '#777', marginBottom: '8px', lineHeight: 1.4 }}>{hint}</div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          type="text" value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            flex: 1, padding: '7px 10px', fontFamily: 'monospace', fontSize: '12px',
            border: '1px solid var(--color-gray-200)', borderRadius: '5px',
            background: 'var(--color-white)', color: 'var(--color-black)',
          }}
        />
        <button className="btn btn-secondary btn-sm" onClick={onSave} style={{ whiteSpace: 'nowrap' }}>
          {saved ? 'Saved' : 'Save'}
        </button>
      </div>
    </div>
  );
}
